import { PriceSortDirection } from '../types/cmsTypes';

export type RankingSource = 'manual' | 'auto';

export type RankedCard<T> = {
  card: T;
  /** Where the position came from: pinned manual block vs price-ranked pool. */
  source: RankingSource;
  /** 1-based position inside its block (Manual · PCT #n / Auto · Price #n). */
  rank: number;
};

/**
 * Orders product cards per CMS-1862: pinned (manually arranged) cards first, in
 * their input order, then the remaining pool. With `fallbackEnabled` the pool is
 * ranked by price in the given direction (cards without a resolvable price sink
 * to the bottom keeping input order); otherwise the pool keeps the input order —
 * fully manual arrangement.
 *
 * Vertical-agnostic: the caller supplies `isPinned` and `getPrice` accessors, so
 * the same ranking can be reused by other verticals later.
 */
export function rankProductCards<T>({
  cards,
  isPinned,
  getPrice,
  fallbackEnabled,
  direction = 'LOW_TO_HIGH',
}: {
  cards: ReadonlyArray<T>;
  isPinned: (card: T) => boolean;
  getPrice: (card: T) => number | undefined;
  fallbackEnabled: boolean;
  direction?: PriceSortDirection;
}): RankedCard<T>[] {
  const manual: T[] = [];
  const pool: T[] = [];

  cards.forEach((card) => {
    (isPinned(card) ? manual : pool).push(card);
  });

  const rankedManual = manual.map<RankedCard<T>>((card, index) => ({
    card,
    source: 'manual',
    rank: index + 1,
  }));

  if (!fallbackEnabled) {
    // Fully manual: the pool keeps the PCT-defined order and stays "manual",
    // continuing the numbering after the pinned block.
    return rankedManual.concat(
      pool.map((card, index) => ({
        card,
        source: 'manual',
        rank: manual.length + index + 1,
      })),
    );
  }

  const sign = direction === 'HIGH_TO_LOW' ? -1 : 1;
  const sortedPool = pool
    // A non-finite price is normalized to `undefined` here so the comparator can
    // never return NaN, whatever a future caller's `getPrice` hands back.
    .map((card, index) => {
      const price = getPrice(card);
      return {
        card,
        index,
        price: typeof price === 'number' && Number.isFinite(price) ? price : undefined,
      };
    })
    .sort((a, b) => {
      if (a.price === undefined && b.price === undefined) {
        return a.index - b.index;
      }
      if (a.price === undefined) return 1;
      if (b.price === undefined) return -1;
      if (a.price === b.price) return a.index - b.index;
      return (a.price - b.price) * sign;
    });

  return rankedManual.concat(
    sortedPool.map(({ card }, index) => ({
      card,
      source: 'auto',
      rank: index + 1,
    })),
  );
}
